import React, { useMemo } from "react";
import {
  reportColumns,
  hiddenRepeatKeys,
  groupKeyFields,
  groupStyles,
  GROUP_LABELS,
  freeQuantityReportColumns,
  productProfitabilityColumns,
  seasonWiseTransactionColumns,
  centralLocationStockColumns
} from "../config/reportColumns";

// --- SHARED STYLES FOR SLEEK UI ---
const tableWrapperStyles = "w-full overflow-hidden bg-white rounded-xl shadow-sm ring-1 ring-slate-200 mt-4";
const tableScrollStyles = "max-h-[calc(100vh-140px)] overflow-y-auto";
const tableStyles = "min-w-full text-sm text-left whitespace-nowrap";
const theadStyles = "sticky top-0 z-20 bg-slate-50 shadow-[0_1px_0_0_theme(colors.slate.200)]";
const thStyles = "px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider";
const trStyles = "border-b border-slate-100 hover:bg-slate-50/80 transition-colors duration-150";
const tdStyles = "px-4 py-3 text-slate-600";
const tfootStyles = "sticky bottom-0 z-10 bg-slate-50 font-semibold shadow-[0_-1px_0_0_theme(colors.slate.200)] text-slate-800";
const emptyStateStyles = "text-center py-12 text-slate-500 bg-slate-50/50";


// Builds the top "super header" row: SL/Products/Pack Sizes span 2 rows,
// Credit/Cash/Grand Total each span across their column groups
function buildGroupHeader(columns) {
  const groups = [];
  let i = 0;
  while (i < columns.length) {
    const col = columns[i];
    if (!col.group) {
      groups.push({ label: null, span: 1, key: `plain-${i}` });
      i += 1;
    } else {
      let span = 0;
      const g = col.group;
      while (i < columns.length && columns[i].group === g) {
        span += 1;
        i += 1;
      }
      groups.push({ label: g, span, key: `${g}-${i}` });
    }
  }
  return groups;
}

const buildRowKey = (row) => groupKeyFields.map((f) => row[f]).join("__");

export const ReportTable = ({ reportData = [] }) => {
  const data = Array.isArray(reportData) ? reportData : [];
  const groupHeader = useMemo(() => buildGroupHeader(reportColumns), []);

  const visibleRowMap = useMemo(() => {
    const map = {};
    const seen = new Set();
    data.forEach((row, index) => {
      const rowKey = buildRowKey(row);
      if (!seen.has(rowKey)) {
        map[index] = true;
        seen.add(rowKey);
      } else {
        map[index] = false;
      }
    });
    return map;
  }, [data]);

  const totals = useMemo(() => {
    const acc = {};
    data.forEach((row, index) => {
      reportColumns.forEach((col) => {
        if (!col.isNumeric) return;
        if (hiddenRepeatKeys.includes(col.key) && !visibleRowMap[index]) return;
        acc[col.key] = (acc[col.key] || 0) + parseFloat(row[col.key] || 0);
      });
    });
    return acc;
  }, [data, visibleRowMap]);

  const formatNumber = (val) =>
    val === "" || val === null || val === undefined
      ? ""
      : Number(val).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

  return (
    <div className={tableWrapperStyles}>
      <div className={tableScrollStyles}>
        <table className={tableStyles}>
<thead className={theadStyles}>
  {/* First Header Row - Group Headers */}
  <tr>
    {reportColumns.map((col, index) => {
      // For non-grouped columns (SL, Products Name, Pack Sizes)
      if (!col.group) {
        return (
          <th key={col.key} rowSpan={2} className={thStyles}>
            {col.header}
          </th>
        );
      }
      
      // For grouped columns - only render group header for first column of each group
      const isFirstInGroup = index === 0 || reportColumns[index - 1].group !== col.group;
      if (isFirstInGroup) {
        const groupSpan = reportColumns.filter(c => c.group === col.group).length;
        return (
          <th
            key={`group-${col.group}`}
            colSpan={groupSpan}
            className={`${thStyles} text-center ${groupStyles[col.group]?.header || ""}`}
          >
            {GROUP_LABELS[col.group]}
          </th>
        );
      }
      return null;
    }).filter(Boolean)}
  </tr>
  
  {/* Second Header Row - Individual Column Headers */}
  <tr>
  {reportColumns.map((col) => {
    // ADD THIS LINE TO SKIP NON-GROUPED COLUMNS
    if (!col.group) return null; 

    return (
      <th
        key={col.key}
        className={`${thStyles} ${col.isNumeric ? "text-right" : "text-left"} ${
          col.group ? groupStyles[col.group]?.header || "" : ""
        }`}
      >
        {col.header}
      </th>
    );
  })}
</tr>
</thead>
          <tbody>
            {data.length > 0 ? (
              <>
                {data.map((row, rowIndex) => (
                  <tr key={rowIndex} className={trStyles}>
                    {reportColumns.map((col) => {
                      let displayValue = row[col.key];

                      if (col.key === "Master_id") {
                        displayValue = rowIndex + 1;
                      } else if (
                        (hiddenRepeatKeys.includes(col.key) || col.key === "products_name") &&
                        !visibleRowMap[rowIndex]
                      ) {
                        displayValue = "";
                      } else if (col.isNumeric) {
                        displayValue = formatNumber(displayValue);
                      }

                      const cellBg =
                        col.group && !hiddenRepeatKeys.includes(col.key)
                          ? groupStyles[col.group]?.cell
                          : "";

                      return (
                        <td
                          key={col.key}
                          className={`${tdStyles} ${col.isNumeric ? "text-right text-slate-700" : "text-left"
                            } ${cellBg}`}
                        >
                          {displayValue}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className={tfootStyles}>
                  {reportColumns.map((col, idx) => (
                    <td
                      key={col.key}
                      className={`px-3 py-2 border border-slate-300 ${col.isNumeric ? "text-right" : "text-left"
                        }`}
                    >
                      {idx === 0 ? "TOTAL" : col.isNumeric ? totals[col.key]?.toFixed(2) : ""}
                    </td>
                  ))}
                </tr>
              </>
            ) : (
              <tr>
                <td colSpan={reportColumns.length} className={emptyStateStyles}>
                  No Data Available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};


export const FreeQtyReportTable = ({ reportData = [] }) => {
  const sortedData = useMemo(() => {
    return [...reportData].sort((a, b) => a.products_name.localeCompare(b.products_name));
  }, [reportData]);

  const isFirstOfGroup = (index, arr) => {
    if (index === 0) return true;
    return arr[index].products_name !== arr[index - 1].products_name;
  };

  // 🆕 Format numeric values to exactly 2 decimal places for display.
  // Leaves blank/empty strings untouched (used for repeated-group rows).
  const formatNumeric = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const num = Number(value);
    return isNaN(num) ? "" : num.toFixed(2);
  };

  const footerTotals = useMemo(() => {
    const totals = {};
    freeQuantityReportColumns.forEach(col => { if (col.isNumeric) totals[col.key] = 0; });
    sortedData.forEach((row, i, arr) => {
      const firstRow = isFirstOfGroup(i, arr);
      freeQuantityReportColumns.forEach(col => {
        if (!col.isNumeric) return;
        if ((col.key === "grand_total_amount" && !firstRow) ||
          (col.key === "total_alt_qty" && !firstRow) ||
          (col.key === "grand_total_qty" && !firstRow)) {
          return;
        }
        totals[col.key] += Number(row[col.key] || 0);
      });
    });
    return totals;
  }, [sortedData]);

  return (
    <div className="w-full h-full overflow-auto border border-slate-200 rounded-lg">
      <table className="min-w-max w-full border-collapse text-sm">
        <thead className="sticky top-0 z-20 bg-slate-100 shadow-sm">
          <tr>
            {freeQuantityReportColumns.map(col => (
              <th
                key={col.key}
                className={`px-4 py-3 font-semibold text-slate-600 whitespace-nowrap border-b border-slate-200 ${col.isNumeric ? "text-right" : "text-left"
                  }`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {sortedData.map((row, rowIndex, arr) => {
            const firstRow = isFirstOfGroup(rowIndex, arr);
            return (
              <tr key={rowIndex} className="border-b border-slate-100 hover:bg-slate-50">
                {freeQuantityReportColumns.map(col => {
                  let displayValue = row[col.key];
                  if (["products_name", "grand_total_amount", "total_alt_qty", "grand_total_qty"].includes(col.key) && !firstRow) {
                    displayValue = "";
                  }
                  if (col.key === "SL") displayValue = rowIndex + 1;

                  // 🆕 Round numeric columns to 2 decimals (skip SL, which is a plain integer index)
                  if (col.isNumeric && col.key !== "SL") {
                    displayValue = formatNumeric(displayValue);
                  }

                  return (
                    <td
                      key={col.key}
                      className={`px-4 py-2.5 whitespace-nowrap ${col.isNumeric ? "text-right text-slate-700 font-medium" : "text-left"
                        }`}
                    >
                      {displayValue}
                    </td>
                  );
                })}
              </tr>
            );
          })}

          {sortedData.length === 0 && (
            <tr>
              <td colSpan={freeQuantityReportColumns.length} className="text-center py-10 text-slate-400">
                No Data Available
              </td>
            </tr>
          )}
        </tbody>

        {sortedData.length > 0 && (
          <tfoot className="sticky bottom-0 z-20 bg-slate-100 border-t-2 border-slate-300 shadow-[0_-1px_4px_rgba(0,0,0,0.06)]">
            <tr>
              {freeQuantityReportColumns.map((col, idx) => (
                <td
                  key={col.key}
                  className={`px-4 py-3.5 font-semibold whitespace-nowrap ${col.isNumeric ? "text-right" : "text-left"
                    }`}
                >
                  {idx === 0 ? "TOTAL" : col.isNumeric ? footerTotals[col.key]?.toFixed(2) : ""}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};


export const ProductProfitabilityReportTable = ({ data = [], loading = false }) => {
  const isFirstOfGroup = (data, index) => {
    if (index === 0) return true;
    return !(data[index].product_name === data[index - 1].product_name && data[index].godown_name === data[index - 1].godown_name);
  };

  const groupedKeys = ["product_name", "godown_name", "total_quantity", "total_amount", "total_consumption", "total_profit"];
  const numericColumns = useMemo(() => productProfitabilityColumns.filter(col => col.isNumeric), []);

  const isCellVisible = (colKey, data, rowIndex) => {
    if (!groupedKeys.includes(colKey)) return true;
    return isFirstOfGroup(data, rowIndex);
  };

  const totals = useMemo(() => {
    const sum = {};
    numericColumns.forEach(col => sum[col.key] = 0);
    data.forEach((row, i) => {
      numericColumns.forEach(col => {
        if (isCellVisible(col.key, data, i)) {
          sum[col.key] += Number(row[col.key] || 0);
        }
      });
    });
    return sum;
  }, [data, numericColumns]);

  return (
    <div className={tableWrapperStyles}>
      <div className={tableScrollStyles}>
        <table className={tableStyles}>
          <thead className={theadStyles}>
            <tr>
              {productProfitabilityColumns.map((col) => (
                <th key={col.key} className={`${thStyles} ${col.isNumeric ? "text-right" : "text-left"}`}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={productProfitabilityColumns.length} className={emptyStateStyles}>Loading report...</td></tr>}
            {!loading && data.length === 0 && <tr><td colSpan={productProfitabilityColumns.length} className={emptyStateStyles}>No records found</td></tr>}
            {!loading && data.map((row, rowIndex) => {
              const first = isFirstOfGroup(data, rowIndex);
              return (
                <tr key={rowIndex} className={trStyles}>
                  {productProfitabilityColumns.map((col) => {
                    const showVal = groupedKeys.includes(col.key) ? first : true;
                    return (
                      <td key={col.key} className={`${tdStyles} ${col.isNumeric ? "text-right text-slate-700" : "text-left"}`}>
                        {showVal ? (row[col.key] ?? "-") : ""}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {!loading && data.length > 0 && (
              <tr className={tfootStyles}>
                {productProfitabilityColumns.map((col, idx) => (
                  <td key={col.key} className={`px-4 py-3.5 ${col.isNumeric ? "text-right" : "text-left"}`}>
                    {idx === 0 ? "TOTAL" : col.isNumeric ? totals[col.key]?.toFixed(2) : ""}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};


export const CustomerProfileTable = ({
  columns = [],
  data = [],
  paymentHistory = [],
  totalsPerProduct = [],
}) => {
  const safeData = Array.isArray(data) ? data : [];
  const safePayment = Array.isArray(paymentHistory) ? paymentHistory : [];
  const safeTotals = Array.isArray(totalsPerProduct) ? totalsPerProduct : [];

  console.log("Payment History:", safePayment);
  console.log("Data:", safeData);

  /* ============================================================
     TOTALS MAP
     ============================================================ */

  const totalsMap = safeTotals.reduce((acc, t) => {
    const key = `${t.bill_no}|${t.bill_type}|${t.bill_date}|${t.products_name}`;

    acc[key] = t;

    return acc;
  }, {});

  /* ============================================================
     PAYMENT MAP
     Group payment history party-wise
     ============================================================ */

  const paymentMap = {};

  safePayment.forEach((p) => {
    const partyName = p.party_name;

    if (partyName) {
      if (!paymentMap[partyName]) {
        paymentMap[partyName] = [];
      }

      paymentMap[partyName].push(p);
    }
  });

  console.log("Payment Map (ledgerwise):", paymentMap);

  /* ============================================================
     SALES GROUP KEY
     ============================================================ */

  const makeGroupKey = (row) =>
    `${row.bill_no}|${row.bill_type}|${row.bill_date}|${row.products_name}`;

  /* ============================================================
     GROUP SALES DATA
     ============================================================ */

  const salesMap = safeData.reduce((acc, s) => {
    const key = makeGroupKey(s);

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(s);

    return acc;
  }, {});

  /* ============================================================
     MERGE SALES + PAYMENT DATA

     IMPORTANT:
     Payment history must NOT determine the number
     of table rows.

     Previously:
       Math.max(salesRows.length, partyPayments.length)

     was creating blank rows when paymentHistory had
     more records than the sales rows.

     Now ONLY salesRows.length determines row count.
     ============================================================ */

  const mergedRows = [];
  const seenParties = new Set();

  Object.keys(salesMap).forEach((groupKey) => {
    const salesRows = salesMap[groupKey] || [];

    if (salesRows.length === 0) {
      return;
    }

    const partyName = salesRows[0]?.party_name;

    const isFirstPartyOccurrence = partyName
      ? !seenParties.has(partyName)
      : false;

    if (partyName && isFirstPartyOccurrence) {
      seenParties.add(partyName);
    }

    /* ------------------------------------------------------------
       Get payments for this party.

       Payment records are NOT used to generate additional rows.
       ------------------------------------------------------------ */

    const partyPayments = isFirstPartyOccurrence
      ? paymentMap[partyName] || []
      : [];

    const totals = totalsMap[groupKey] || {};

    /* ------------------------------------------------------------
       ONLY SALES ROWS CREATE TABLE ROWS
       ------------------------------------------------------------ */

    for (let i = 0; i < salesRows.length; i++) {
      const salesData = salesRows[i] || {};

      /*
       * Payment information is displayed only on the first
       * occurrence of the party and only on the first row.
       *
       * This prevents payment history from generating
       * blank rows.
       */
      const paymentData =
        isFirstPartyOccurrence && i === 0
          ? partyPayments[0] || {}
          : {};

      mergedRows.push({
        data: salesData,
        payment: paymentData,
        totals,
        isFirstRow: i === 0,
        groupKey,
        isPaymentVisible: isFirstPartyOccurrence && i === 0,
        partyName,
      });
    }
  });

  console.log("Merged Rows:", mergedRows);

  /* ============================================================
     NUMBER FORMATTER
     ============================================================ */

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") {
      return "";
    }

    const num = Number(value);

    return isNaN(num) ? "" : num.toFixed(2);
  };

  /* ============================================================
     FOOTER TOTALS
     ============================================================ */

  const totalsFooter = {};

  columns.forEach((col) => {
    if (!col.isNumeric) {
      return;
    }

    totalsFooter[col.key] = mergedRows.reduce((sum, row) => {
      let value;

      /* ----------------------------------------------------------
         These values exist at group level.

         They should only be counted once per grouped product.
         ---------------------------------------------------------- */

      if (
        [
          "total_quantity",
          "total_free_qty",
          "total_alt_qty",
          "grand_total",
        ].includes(col.key)
      ) {
        if (!row.isFirstRow) {
          return sum;
        }

        value =
          col.key === "grand_total"
            ? row.totals.total_amount
            : row.totals[col.key];
      }

      /* ----------------------------------------------------------
         Payment totals
         ---------------------------------------------------------- */

      else if (
        ["paid_amount", "discount_amount"].includes(col.key)
      ) {
        value = row.payment[col.key];
      }

      /* ----------------------------------------------------------
         Normal sales/payment values
         ---------------------------------------------------------- */

      else {
        value =
          row.data[col.key] ??
          row.payment[col.key];
      }

      return sum + Number(value || 0);
    }, 0);
  });

  /* ============================================================
     HIDE REPEATED GROUP VALUES
     ============================================================ */

  const shouldHide = (row, colKey) => {
    return (
      [
        "bill_no",
        "products_name",
        "bill_type",
        "bill_date",
        "total_quantity",
        "total_free_qty",
        "total_alt_qty",
        "grand_total",
      ].includes(colKey) &&
      !row.isFirstRow
    );
  };

  /* ============================================================
     TABLE
     ============================================================ */

  return (
    <div className="w-full overflow-hidden border border-gray-200 rounded-lg shadow-sm">

      <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-220px)]">

        <div className="min-w-max">

          <table className="w-full text-sm border-collapse">

            {/* ==================================================
                HEADER
                ================================================== */}

            <thead className="bg-slate-100 sticky top-0 z-10">

              <tr>

                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`
                      px-4
                      py-3.5
                      text-xs
                      font-semibold
                      uppercase
                      tracking-wider
                      ${
                        col.isNumeric
                          ? "text-right"
                          : "text-left"
                      }
                      border-b
                      border-slate-200
                      whitespace-nowrap
                      sticky
                      top-0
                      bg-slate-100
                    `}
                    style={{
                      minWidth: col.width || "120px",
                    }}
                  >
                    {col.header}
                  </th>
                ))}

              </tr>

            </thead>

            {/* ==================================================
                BODY
                ================================================== */}

            <tbody>

              {mergedRows.length === 0 ? (

                <tr>

                  <td
                    colSpan={columns.length}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    No data found
                  </td>

                </tr>

              ) : (

                mergedRows.map((row, idx) => (

                  <tr
                    key={idx}
                    className={`
                      hover:bg-slate-50
                      transition-colors
                      ${
                        row.isFirstRow
                          ? "border-t border-slate-200 bg-white"
                          : "border-none bg-slate-50/30"
                      }
                    `}
                  >

                    {columns.map((col) => {

                      /* ------------------------------------------------
                         Hide repeated grouped values
                         ------------------------------------------------ */

                      if (shouldHide(row, col.key)) {

                        return (
                          <td
                            key={col.key}
                            className="px-4 py-2.5"
                          />
                        );

                      }

                      let value;

                      /* ------------------------------------------------
                         PAYMENT COLUMNS
                         ------------------------------------------------ */

                      if (
                        [
                          "payment_date",
                          "paid_amount",
                          "discount_amount",
                          "receipt_no",
                          "opening_balance",
                          "closing_balance",
                        ].includes(col.key)
                      ) {

                        if (
                          row.isPaymentVisible &&
                          row.payment
                        ) {
                          value =
                            row.payment[col.key] ?? "";
                        } else {
                          value = "";
                        }

                      }

                      /* ------------------------------------------------
                         TOTAL QUANTITY COLUMNS
                         ------------------------------------------------ */

                      else if (
                        [
                          "total_quantity",
                          "total_free_qty",
                          "total_alt_qty",
                        ].includes(col.key)
                      ) {

                        value = row.totals[col.key];

                      }

                      /* ------------------------------------------------
                         GRAND TOTAL
                         ------------------------------------------------ */

                      else if (col.key === "grand_total") {

                        value = row.totals.total_amount;

                      }

                      /* ------------------------------------------------
                         NORMAL COLUMNS
                         ------------------------------------------------ */

                      else {

                        value =
                          row.data[col.key] ??
                          row.payment[col.key] ??
                          "";

                      }

                      /* ------------------------------------------------
                         CELL
                         ------------------------------------------------ */

                      return (
                        <td
                          key={col.key}
                          className={`
                            px-4
                            py-2.5
                            ${
                              col.isNumeric
                                ? "text-right font-mono text-sm"
                                : "text-left"
                            }
                          `}
                          style={{
                            minWidth:
                              col.width || "120px",
                          }}
                        >
                          {col.isNumeric
                            ? formatNumber(value)
                            : value ?? ""}
                        </td>
                      );

                    })}

                  </tr>

                ))

              )}

            </tbody>

            {/* ==================================================
                FOOTER TOTALS
                ================================================== */}

            {mergedRows.length > 0 && (

              <tfoot className="bg-slate-50 border-t-2 border-slate-200 sticky bottom-0">

                <tr>

                  {columns.map((col, idx) => (

                    <td
                      key={col.key}
                      className={`
                        px-4
                        py-3.5
                        font-semibold
                        ${
                          col.isNumeric
                            ? "text-right font-mono"
                            : "text-left"
                        }
                      `}
                      style={{
                        minWidth:
                          col.width || "120px",
                      }}
                    >

                      {col.isNumeric

                        ? (
                            totalsFooter[col.key] || 0
                          ).toFixed(2)

                        : idx === 0

                          ? "Total"

                          : ""

                      }

                    </td>

                  ))}

                </tr>

              </tfoot>

            )}

          </table>

        </div>

      </div>

    </div>
  );
};

export const SeasonWiseTransactionTable = ({ reportData = [] }) => {
  const data = Array.isArray(reportData) ? reportData : [];
  const numericColumns = useMemo(() => seasonWiseTransactionColumns.filter(col => col.isNumeric), []);
  const getGroupKey = (row) => `${row.bill_date}|${row.products_name}|${row.bill_type}|${row.bill_no}|${row.party_name}`;

  const totals = useMemo(() => {
    const sum = {};
    numericColumns.forEach(col => sum[col.key] = 0);
    data.forEach((row, rowIndex) => {
      const currentKey = getGroupKey(row);
      const prevKey = rowIndex > 0 ? getGroupKey(data[rowIndex - 1]) : null;
      const isFirstRowOfGroup = currentKey !== prevKey;

      numericColumns.forEach(col => {
        const isHidden = !isFirstRowOfGroup && ["bill_date", "bill_no", "bill_type", "party_name", "total_quantity", "total_amount"].includes(col.key);
        if (!isHidden) sum[col.key] += Number(row[col.key]) || 0;
      });
    });
    return sum;
  }, [data, numericColumns]);

  return (
    <div className={tableWrapperStyles}>
      <div className={tableScrollStyles}>
        <table className={tableStyles}>
          <thead className={theadStyles}>
            <tr>
              {seasonWiseTransactionColumns.map(col => (
                <th key={col.key} className={`${thStyles} ${col.isNumeric ? "text-right" : "text-left"}`}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((row, rowIndex) => {
                const currentKey = getGroupKey(row);
                const prevKey = rowIndex > 0 ? getGroupKey(data[rowIndex - 1]) : null;
                const isFirstRowOfGroup = currentKey !== prevKey;

                return (
                  <tr key={rowIndex} className={trStyles}>
                    {seasonWiseTransactionColumns.map(col => {
                      const shouldHide = !isFirstRowOfGroup && ["bill_date", "bill_no", "bill_type", "party_name", "total_quantity", "total_amount", "products_name", "region_name"].includes(col.key);
                      return (
                        <td key={col.key} className={`${tdStyles} ${col.isNumeric ? "text-right text-slate-700" : "text-left"}`}>
                          {shouldHide ? "" : col.key === "sl" ? rowIndex + 1 : row[col.key] ?? "-"}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr><td colSpan={seasonWiseTransactionColumns.length} className={emptyStateStyles}>No Data Available</td></tr>
            )}
          </tbody>
          {data.length > 0 && (
            <tfoot className={tfootStyles}>
              <tr>
                {seasonWiseTransactionColumns.map((col, idx) => (
                  <td key={col.key} className={`px-4 py-3.5 ${col.isNumeric ? "text-right" : "text-left"}`}>
                    {col.isNumeric ? (totals[col.key] || 0).toFixed(3) : idx === 0 ? "Total" : ""}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};


export const CentralLocationStockTable = ({ reportData = [] }) => {
  const data = Array.isArray(reportData) ? reportData : [];
  const numericColumns = useMemo(() => centralLocationStockColumns.filter(col => col.isNumeric), []);

  const processedData = useMemo(() => {
    let lastKey = "";
    return data.map((row) => {
      const currentKey = `${row.product_name}__${row.godown_name}`;
      const isFirstRow = currentKey !== lastKey;
      lastKey = currentKey;
      return { ...row, _showGroup: isFirstRow };
    });
  }, [data]);

  const totals = useMemo(() => {
    const sum = {};
    numericColumns.forEach(col => sum[col.key] = 0);
    processedData.forEach(row => {
      numericColumns.forEach(col => {
        const hideIfGrouped = !row._showGroup && ["product_name", "godown_name", "grand_total_qty", "total_alt_qty", "total_amount"].includes(col.key);
        if (!hideIfGrouped) sum[col.key] += Number(row[col.key]) || 0;
      });
    });
    return sum;
  }, [processedData, numericColumns]);

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "";
    return Number(value).toFixed(2);
  };

  return (
    <div className={tableWrapperStyles}>
      <div className={tableScrollStyles}>
        <table className={tableStyles}>
          <thead className={theadStyles}>
            <tr>
              {centralLocationStockColumns.map(col => (
                <th key={col.key} className={`${thStyles} ${col.isNumeric ? "text-right" : "text-left"}`}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {processedData.length > 0 ? (
              <>
                {processedData.map((row, rowIndex) => (
                  <tr key={rowIndex} className={trStyles}>
                    {centralLocationStockColumns.map(col => {
                      const hideIfGrouped = !row._showGroup && ["product_name", "godown_name", "grand_total_qty", "total_alt_qty", "total_amount"].includes(col.key);
                      const cellValue = col.key === "sl" ? rowIndex + 1 : hideIfGrouped ? "" : col.isNumeric ? formatNumber(row[col.key]) : row[col.key] ?? "-";

                      return (
                        <td key={col.key} className={`${tdStyles} ${col.isNumeric ? "text-right text-slate-700" : "text-left"}`}>
                          {cellValue}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className={tfootStyles}>
                  {centralLocationStockColumns.map((col, idx) => (
                    <td key={col.key} className={`px-4 py-3.5 ${col.isNumeric ? "text-right" : "text-left"}`}>
                      {idx === 0 ? "TOTAL" : col.isNumeric ? formatNumber(totals[col.key]) : ""}
                    </td>
                  ))}
                </tr>
              </>
            ) : (
              <tr><td colSpan={centralLocationStockColumns.length} className={emptyStateStyles}>No Data Available</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};